import { useEffect, useRef, useState, type FormEvent } from 'react';
import { api, ApiError, type Me } from '../api';
import { ErrorBanner } from '../components/ui';
import { Field } from '../components/Field';
import { Alert, Button, cardClass, fieldClass, selectClass } from '@ledgerloom/ui';

// Sign in, with a second step when two-factor is on and a choice when the
// email and password match in more than one workspace. Patterns:
// authentication/login, forms/password, authentication/two-factor,
// forms/code-confirmation, forms/radio for the workspace, and forms/select for
// the development accounts, which only a local server with a dev login lists.
export function Login({ onSignedIn }: { onSignedIn: (me: Me) => void }) {
  // The welcome email's link names the workspace and the email.
  const [link] = useState(() => new URLSearchParams(window.location.search));
  const [email, setEmail] = useState(link.get('email') ?? '');
  const [password, setPassword] = useState('');
  const [dev, setDev] = useState<DevLogin | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(false);
  const [totp, setTotp] = useState('');
  const [needsCode, setNeedsCode] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [workspaces, setWorkspaces] = useState<Array<{ slug: string; name: string }> | null>(null);
  const [workspace, setWorkspace] = useState(link.get('workspace') ?? '');
  const codeRef = useRef<HTMLInputElement>(null);

  useEffect(() => { document.title = 'Sign in · Mazu'; }, []);
  // A local server with LL_DEV_LOGIN_PASSWORD lists its accounts; everywhere else this answers 404.
  useEffect(() => {
    api<DevLogin>('/v1/auth/dev-accounts').then((d) => {
      setDev(d);
      if (d.email) { setEmail((v) => v || d.email!); setPassword((v) => v || d.password); }
    }, () => undefined);
  }, []);
  useEffect(() => { if (needsCode) codeRef.current?.focus(); }, [needsCode]);

  async function signIn(creds: { email: string; password: string; workspace?: string }) {
    setBusy(true);
    setError(null);
    try {
      const res = await api<{ user: Me }>('/v1/auth/login', { body: { ...creds, workspace: creds.workspace || undefined, totp: totp || undefined, remember } });
      onSignedIn(res.user);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'mfa_required') setNeedsCode(true);
      else if (err instanceof ApiError && err.code === 'choose_workspace' && Array.isArray(err.details)) {
        setWorkspaces(err.details as Array<{ slug: string; name: string }>);
        setWorkspace((err.details as Array<{ slug: string }>)[0]?.slug ?? '');
      } else setError(err);
    } finally {
      setBusy(false);
    }
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    void signIn({ email, password, workspace: workspaces || link.get('workspace') ? workspace : undefined });
  }

  // A wrong code fails the code field; anything else fails the credentials.
  const failed = error && !(error instanceof ApiError && error.code === 'password_expired') ? (needsCode ? 'code' : 'credentials') : null;

  return (
    <main className="login" id="main">
      <div className="stack login-col">
      <form className={`${cardClass} card-pad stack`} onSubmit={submit}>
        <div className="row"><img src="/icon.svg" alt="" width={32} height={32} /><h1>Sign in to Mazu</h1></div>
        <p className="muted" style={{ margin: 0 }}>Review what your agents propose.</p>
        <ErrorBanner error={error} focusOnShow />
        <Field label="Email" error={failed === 'credentials' ? 'Check your email and password.' : null} required>
          <input className={fieldClass} type="email" inputMode="email" autoComplete="username" value={email} onChange={(e) => { setEmail(e.target.value); setWorkspaces(null); }} autoFocus disabled={needsCode} />
        </Field>
        <Field label="Password" error={failed === 'credentials' ? 'Check your email and password.' : null} required>
          <PasswordInput value={password} onChange={setPassword} show={showPassword} onToggle={() => setShowPassword((v) => !v)} disabled={needsCode} />
        </Field>
        <label className="row small" style={{ gap: 8, flexWrap: 'nowrap', alignItems: 'flex-start' }}>
          <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} aria-describedby="remember-hint" style={{ marginTop: 3 }} />
          <span>Keep me signed in on this device<span id="remember-hint" className="faint" style={{ display: 'block' }}>For 30 days. Leave this off on a shared computer.</span></span>
        </label>
        {workspaces && (
          <fieldset className="stack" style={{ border: 0, padding: 0, margin: 0, gap: 6 }}>
            <legend className="small" style={{ fontWeight: 600, marginBottom: 6 }}>This email is in more than one workspace. Which one?</legend>
            {workspaces.map((w) => (
              <label key={w.slug} className="row small" style={{ gap: 8, flexWrap: 'nowrap' }}>
                <input type="radio" name="workspace" value={w.slug} checked={workspace === w.slug} onChange={() => setWorkspace(w.slug)} />
                <span>{w.name} <span className="faint">({w.slug})</span></span>
              </label>
            ))}
          </fieldset>
        )}
        {needsCode && (
          <>
            <Alert tone="info" role="status">Two-factor sign-in is on for this account. Enter the code from your authenticator app.</Alert>
            <Field label="Authentication code" hint="6 digits. The code changes every 30 seconds." error={failed === 'code' ? 'That code did not work. Try the newest one.' : null} required>
              <input ref={codeRef} className={fieldClass} inputMode="numeric" autoComplete="one-time-code" pattern="\d{6}" maxLength={6} value={totp} onChange={(e) => setTotp(e.target.value.replace(/\D/g, ''))} style={{ maxWidth: 180, letterSpacing: '0.2em' }} />
            </Field>
          </>
        )}
        <Button type="submit" loading={busy}>{busy ? 'Signing in…' : needsCode ? 'Verify and sign in' : 'Sign in'}</Button>
        <p className="faint" style={{ margin: 0 }}>Forgot your password? Ask an admin on your team to reset it.</p>
      </form>
      {dev?.accounts.length ? <DevAccounts accounts={dev.accounts} preferred={dev.email} busy={busy} onPick={(a) => { setEmail(a.email); setPassword(dev.password); void signIn({ email: a.email, password: dev.password, workspace: a.workspace }); }} /> : null}
      </div>
    </main>
  );
}

type DevAccount = { workspace: string; workspace_name: string; email: string; name: string; role: string; mfa: boolean };
type DevLogin = { email: string | null; password: string; accounts: DevAccount[] };
const DEV_PICK = 'll.devAccount';

// Development only: every account on the local stack, grouped by workspace,
// all with the LL_DEV_LOGIN_PASSWORD from `pnpm dev:accounts`.
// Opens on the last pick in this browser, else on LL_DEV_LOGIN_EMAIL's account, else the first.
function DevAccounts({ accounts, preferred, busy, onPick }: { accounts: DevAccount[]; preferred: string | null; busy: boolean; onPick: (a: DevAccount) => void }) {
  const key = (a: DevAccount) => `${a.workspace}/${a.email}`;
  const [pick, setPick] = useState(() => {
    let saved: string | null = null;
    try { saved = localStorage.getItem(DEV_PICK); } catch { /* storage blocked */ }
    if (accounts.some((a) => key(a) === saved)) return saved!;
    return key(accounts.find((a) => a.email === preferred) ?? accounts[0]!);
  });
  const groups = [...new Set(accounts.map((a) => a.workspace))];
  const chosen = accounts.find((a) => key(a) === pick)!;
  return (
    <section className={`${cardClass} card-pad stack`} aria-labelledby="dev-h">
      <h2 id="dev-h" style={{ margin: 0, fontSize: '1rem' }}>Development accounts</h2>
      <p className="faint small" style={{ margin: 0 }}>Every account on the local stack. Only a local server with a dev login shows this.</p>
      <form className="stack" onSubmit={(e) => {
        e.preventDefault();
        try { localStorage.setItem(DEV_PICK, pick); } catch { /* storage blocked */ }
        onPick(chosen);
      }}>
        <Field label="Account">
          <select className={selectClass} value={pick} onChange={(e) => setPick(e.target.value)}>
            {groups.map((g) => {
              const inGroup = accounts.filter((a) => a.workspace === g);
              return (
                <optgroup key={g} label={`${inGroup[0]!.workspace_name} (${g})`}>
                  {inGroup.map((a) => <option key={key(a)} value={key(a)}>{a.name}, {a.role}{a.mfa ? ', two-factor' : ''}</option>)}
                </optgroup>
              );
            })}
          </select>
        </Field>
        <p className="faint small" style={{ margin: 0 }}>{chosen.email}</p>
        <Button type="submit" variant="secondary" disabled={busy}>Sign in as {chosen.name}</Button>
      </form>
    </section>
  );
}

export function PasswordInput({ value, onChange, show, onToggle, autoComplete = 'current-password', ...rest }: {
  value: string; onChange: (v: string) => void; show: boolean; onToggle: () => void; disabled?: boolean; autoComplete?: string;
  id?: string; 'aria-describedby'?: string; 'aria-invalid'?: boolean; required?: boolean;
}) {
  return (
    <div className="row" style={{ gap: 6, flexWrap: 'nowrap' }}>
      <input className={fieldClass} type={show ? 'text' : 'password'} autoComplete={autoComplete} value={value} onChange={(e) => onChange(e.target.value)} {...rest} />
      <Button type="button" variant="ghost" size="sm" aria-pressed={show} onClick={onToggle} aria-label={show ? 'Hide password' : 'Show password'}>{show ? 'Hide' : 'Show'}</Button>
    </div>
  );
}
